import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useNavigate } from 'react-router-dom';
import GlobalTemplateManager from '../components/GlobalTemplateManager';
import { BillingDashboard } from '../components/admin/BillingDashboard';
import { StripeConfiguration } from '../components/admin/StripeConfiguration';
import { CompanyBillingManagement } from '../components/admin/CompanyBillingManagement';
import { SupportTicketManager } from '../components/admin/SupportTicketManager';
import AgentMailboxAdmin from '../components/admin/AgentMailboxAdmin';
import AcordRepositoryAdmin from '../components/admin/AcordRepositoryAdmin';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { formatWorkflowLabel } from '../constants/betaToolLabels';

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  createdAt: string;
  twoFactorEnabled?: boolean;
  role?: string;
  lineOfBusiness?: 'personal' | 'commercial' | 'both';
  consumesLicense?: boolean;
  company?: {
    name: string;
    domain: string;
  };
}

interface Company {
  id: string;
  name: string;
  domain: string;
  licenseCount: number;
  isLocked: boolean;
  ams360Enabled: boolean;
  used: number;
  available: number;
  admins: AdminUser[];
  createdAt: string;
  updatedAt: string;
  subscriptionStatus?: string;
}

interface TokenUsageStats {
  companyId: string;
  companyName: string;
  totalTokensIn: number;
  totalTokensOut: number;
  totalTokens: number;
  userCount: number;
  requestCount: number;
  totalApiCalls: number;
}

interface ModelOption {
  id: string;
  name: string;
  provider: string;
}

interface WorkflowSetting {
  id: string;
  workflowType: string;
  aiModel: string;
  quickAiModel: string | null;
  deepAiModel: string | null;
  lineOfBusiness: 'personal' | 'commercial' | 'both';
  updatedBy: string | null;
  updatedAt: string;
}

interface ModelSettingsGroup {
  id: string;
  label: string;
  kind: 'suite' | 'category' | 'platform';
  reportTypes: string[];
  routerTypes: string[];
}

interface CompanyTokenDetails {
  totalTokensIn: number;
  totalTokensOut: number;
  totalTokens: number;
  requestCount: number;
  userCount: number;
  period: string;
  byUser: Record<string, {
    userId: string;
    userName: string;
    tokensIn: number;
    tokensOut: number;
    totalTokens: number;
  }>;
}

interface ActiveUser {
  sessionId: string;
  user: AdminUser;
  deviceName: string;
  ipAddress: string;
  location?: string;
  lastActivity: string;
  sessionStarted: string;
}

interface ActiveUsersResponse {
  success: boolean;
  data: ActiveUser[];
  summary: {
    totalActiveSessions: number;
    uniqueActiveUsers: number;
    timeframe: string;
    timestamp: string;
  };
}

interface WorkflowCategory {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
  toolCount: number;
}

interface WorkflowToolWithCategory {
  workflowType: string;
  displayName: string;
  aiModel: string;
  lineOfBusiness: 'personal' | 'commercial' | 'both';
  categoryId: string | null;
  isHidden: boolean;
  visibilityTagId: string | null;
  visibilityTagName?: string | null;
}

interface VisibilityTag {
  id: string;
  name: string;
  description: string | null;
  displayOrder: number;
}

interface CompanyWithTags {
  companyId: string;
  companyName: string;
  tagIds: string[];
  tags: VisibilityTag[];
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const SystemAdmin: React.FC = () => {
  const { user, token } = useAuth();
  const { startImpersonation } = useImpersonation();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [formData, setFormData] = useState({ email: '', firstName: '', lastName: '' });
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [showCreateCompany, setShowCreateCompany] = useState<boolean>(false);
  const [newCompanyData, setNewCompanyData] = useState({ name: '', domain: '', licenseCount: 0 });
  const [showGlobalTemplates, setShowGlobalTemplates] = useState<boolean>(false);
  const [showTokenUsage, setShowTokenUsage] = useState<boolean>(false);
  const [tokenStats, setTokenStats] = useState<TokenUsageStats[]>([]);
  const [expandedCompanies, setExpandedCompanies] = useState<Set<string>>(new Set());
  const [companyTokenDetails, setCompanyTokenDetails] = useState<Record<string, CompanyTokenDetails>>({});
  const [allUsers, setAllUsers] = useState<AdminUser[]>([]);
  const [showAllUsers, setShowAllUsers] = useState<boolean>(false);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');
  const [collapsedCompanies, setCollapsedCompanies] = useState<Set<string>>(new Set());
  const [showCreateUser, setShowCreateUser] = useState<boolean>(false);
  const [selectedCompanyForUser, setSelectedCompanyForUser] = useState<string | null>(null);
  const [newUserData, setNewUserData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: 'user'
  });
  const [showActiveUsers, setShowActiveUsers] = useState<boolean>(false);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [activeUsersSummary, setActiveUsersSummary] = useState<any>(null);
  const [activeUsersLoading, setActiveUsersLoading] = useState<boolean>(false);
  const [showBilling, setShowBilling] = useState<boolean>(false);
  const [showSupportTickets, setShowSupportTickets] = useState<boolean>(false);
  const [showAgentMailbox, setShowAgentMailbox] = useState<boolean>(false);
  const [showAcordRepo, setShowAcordRepo] = useState<boolean>(false);
  const features = useFeatureFlags();
  const [showStripeConfig, setShowStripeConfig] = useState<boolean>(false);
  const [billingView, setBillingView] = useState<'dashboard' | 'config'>('dashboard');
  const [expandedBillingCompany, setExpandedBillingCompany] = useState<string | null>(null);
  const [showWorkflowSettings, setShowWorkflowSettings] = useState<boolean>(false);
  const [workflowSettings, setWorkflowSettings] = useState<WorkflowSetting[]>([]);
  const [availableModels, setAvailableModels] = useState<Record<string, ModelOption[]>>({});
  const [modelGroups, setModelGroups] = useState<ModelSettingsGroup[]>([]);
  const [expandedModelGroups, setExpandedModelGroups] = useState<Set<string>>(new Set());
  const [savingModelGroup, setSavingModelGroup] = useState<string | null>(null);
  const [workflowSettingsLoading, setWorkflowSettingsLoading] = useState<boolean>(false);
  const [tokenUsagePeriod, setTokenUsagePeriod] = useState<string>('');

  // Workflow Category Management State
  const [showCategoryManager, setShowCategoryManager] = useState<boolean>(false);
  const [workflowCategories, setWorkflowCategories] = useState<WorkflowCategory[]>([]);
  const [toolsWithCategories, setToolsWithCategories] = useState<WorkflowToolWithCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [editingCategory, setEditingCategory] = useState<WorkflowCategory | null>(null);
  const [newCategoryData, setNewCategoryData] = useState({ name: '', description: '', displayOrder: 0 });

  // Visibility Tag State
  const [visibilityTags, setVisibilityTags] = useState<VisibilityTag[]>([]);
  const [companiesWithTags, setCompaniesWithTags] = useState<CompanyWithTags[]>([]);
  const [showCompanyVisibilityManager, setShowCompanyVisibilityManager] = useState<boolean>(false);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/companies`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch companies');
      setCompanies(data.companies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchTokenUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/token-usage`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch token usage');
      setTokenStats(data.data || []);
      if (data.period) {
        setTokenUsagePeriod(data.period);
      }
    } catch (err) {
      console.error('Failed to fetch token usage:', err);
    }
  }, [token]);

  const fetchWorkflowSettings = useCallback(async () => {
    setWorkflowSettingsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/workflow-settings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch workflow settings');
      setWorkflowSettings(data.settings || []);
      setAvailableModels(data.availableModels && !Array.isArray(data.availableModels) ? data.availableModels : {});
      setModelGroups(Array.isArray(data.groups) ? data.groups : []);
    } catch (err) {
      console.error('Failed to fetch workflow settings:', err);
      alert('Failed to fetch workflow settings');
    } finally {
      setWorkflowSettingsLoading(false);
    }
  }, [token]);

  // Model <option>s grouped by provider. The list comes from the backend
  // catalog (role + research exclusion already applied). For a group of
  // workflow types only models valid for EVERY member are offered. The
  // current value is always kept selectable so a legacy id never renders blank.
  const renderModelOptions = (workflowTypes: string | string[], current: string | null) => {
    const types = Array.isArray(workflowTypes) ? workflowTypes : [workflowTypes];
    const [first, ...others] = types;
    const models = (availableModels[first] || []).filter((m) =>
      others.every((t) => (availableModels[t] || []).some((o) => o.id === m.id))
    );
    const groups: Array<{ label: string; provider: string }> = [
      { label: 'Gemini', provider: 'gemini' },
      { label: 'Claude', provider: 'claude' },
    ];
    const known = models.some((m) => m.id === current);
    return (
      <>
        {current && !known && <option value={current}>{current}</option>}
        {groups.map((g) => {
          const items = models.filter((m) => m.provider === g.provider);
          if (items.length === 0) return null;
          return (
            <optgroup key={g.provider} label={g.label}>
              {items.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </optgroup>
          );
        })}
      </>
    );
  };

  // Shared value across a set of settings rows, or '' when they differ ("Mixed").
  const commonSettingValue = (types: string[], field: 'aiModel' | 'quickAiModel' | 'deepAiModel' | 'lineOfBusiness'): string => {
    const values = types
      .map((t) => workflowSettings.find((s) => s.workflowType === t))
      .filter(Boolean)
      .map((s) => (s as WorkflowSetting)[field] || '');
    if (values.length === 0) return '';
    return values.every((v) => v === values[0]) ? values[0] : '';
  };

  const updateWorkflowSettingsGroup = useCallback(async (
    groupId: string,
    workflowTypes: string[],
    updates: { aiModel?: string; quickAiModel?: string | null; deepAiModel?: string | null; lineOfBusiness?: string }
  ) => {
    if (workflowTypes.length === 0) return false;
    setSavingModelGroup(groupId);
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/workflow-settings/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ workflowTypes, ...updates }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update workflow settings');
      await fetchWorkflowSettings();
      return true;
    } catch (err) {
      console.error('Failed to update workflow settings group:', err);
      alert(err instanceof Error ? err.message : 'Failed to update workflow settings');
      return false;
    } finally {
      setSavingModelGroup(null);
    }
  }, [token, fetchWorkflowSettings]);

  const updateWorkflowSetting = useCallback(async (workflowType: string, updates: { aiModel?: string; quickAiModel?: string | null; deepAiModel?: string | null; lineOfBusiness?: string }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/workflow-settings/${workflowType}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update workflow setting');

      // Refresh settings
      await fetchWorkflowSettings();
      return true;
    } catch (err) {
      console.error('Failed to update workflow setting:', err);
      alert(err instanceof Error ? err.message : 'Failed to update workflow setting');
      return false;
    }
  }, [token, fetchWorkflowSettings]);

  const fetchAllUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/users`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch users');
      setAllUsers(data.users || []);
    } catch (err) {
      console.error('Failed to fetch all users:', err);
      alert('Failed to fetch users');
    } finally {
      setUsersLoading(false);
    }
  }, [token]);

  const updateUserLineOfBusiness = useCallback(async (userId: string, lineOfBusiness: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/users/${userId}/line-of-business`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ lineOfBusiness }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update user line of business');

      // Refresh users list
      await fetchAllUsers();
      return true;
    } catch (err) {
      console.error('Failed to update user line of business:', err);
      alert(err instanceof Error ? err.message : 'Failed to update user line of business');
      return false;
    }
  }, [token, fetchAllUsers]);

  const fetchActiveUsers = useCallback(async () => {
    setActiveUsersLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/active-users?minutes=30`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch active users');
      setActiveUsers(data.data || []);
      setActiveUsersSummary(data.summary || null);
    } catch (err) {
      console.error('Failed to fetch active users:', err);
      alert('Failed to fetch active users');
    } finally {
      setActiveUsersLoading(false);
    }
  }, [token]);

  // Workflow Category Management Functions
  const fetchWorkflowCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      // Fetch categories and tools (required)
      const [categoriesRes, toolsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/sysadmin/workflow-categories`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/api/sysadmin/workflow-categories/tools`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
      ]);

      const categoriesData = await categoriesRes.json();
      const toolsData = await toolsRes.json();

      if (!categoriesRes.ok) throw new Error(categoriesData.error || 'Failed to fetch categories');
      if (!toolsRes.ok) throw new Error(toolsData.error || 'Failed to fetch tools');

      setWorkflowCategories(categoriesData.categories || []);
      setToolsWithCategories(toolsData.tools || []);

      // Fetch visibility tags separately (optional - may not exist if migration hasn't run)
      try {
        const tagsRes = await fetch(`${API_BASE_URL}/api/sysadmin/visibility-tags`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (tagsRes.ok) {
          const tagsData = await tagsRes.json();
          setVisibilityTags(tagsData.tags || []);
        } else {
          console.warn('Visibility tags not available (migration may not have run yet)');
          setVisibilityTags([]);
        }
      } catch (tagErr) {
        console.warn('Failed to fetch visibility tags:', tagErr);
        setVisibilityTags([]);
      }
    } catch (err) {
      console.error('Failed to fetch workflow categories:', err);
      alert('Failed to fetch workflow categories');
    } finally {
      setCategoriesLoading(false);
    }
  }, [token]);

  // Fetch companies with their visibility tags
  const fetchCompaniesWithTags = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/companies-with-visibility-tags`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch companies with tags');
      setCompaniesWithTags(data.companies || []);
    } catch (err) {
      console.error('Failed to fetch companies with tags:', err);
    }
  }, [token]);

  // Update company visibility tags
  const updateCompanyVisibilityTags = useCallback(async (companyId: string, tagIds: string[]) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/companies/${companyId}/visibility-tags`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ tagIds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update company tags');
      await fetchCompaniesWithTags();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error updating company tags');
    }
  }, [token, fetchCompaniesWithTags]);

  const createWorkflowCategory = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/workflow-categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newCategoryData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create category');

      setNewCategoryData({ name: '', description: '', displayOrder: 0 });
      await fetchWorkflowCategories();
      alert('Category created successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error creating category');
    }
  }, [token, newCategoryData, fetchWorkflowCategories]);

  const updateWorkflowCategory = useCallback(async (categoryId: string, updates: { name?: string; description?: string; displayOrder?: number }) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/workflow-categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update category');

      setEditingCategory(null);
      await fetchWorkflowCategories();
      alert('Category updated successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error updating category');
    }
  }, [token, fetchWorkflowCategories]);

  const deleteWorkflowCategory = useCallback(async (categoryId: string) => {
    if (!window.confirm('Are you sure you want to delete this category? Tools in this category will become uncategorized.')) {
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/workflow-categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to delete category');

      await fetchWorkflowCategories();
      alert('Category deleted successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error deleting category');
    }
  }, [token, fetchWorkflowCategories]);

  const assignToolToCategory = useCallback(async (
    workflowType: string,
    categoryId: string | null,
    isHidden?: boolean,
    visibilityTagId?: string | null
  ) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/workflow-categories/assign-tool`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          workflowType,
          categoryId,
          ...(isHidden !== undefined && { isHidden }),
          ...(visibilityTagId !== undefined && { visibilityTagId }),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to assign tool');

      await fetchWorkflowCategories();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error assigning tool');
    }
  }, [token, fetchWorkflowCategories]);

  const exportUserEmails = useCallback(async (format: 'csv' | 'json' = 'csv', includeInactive = false) => {
    try {
      const url = `${API_BASE_URL}/api/sysadmin/export-emails?format=${format}&includeInactive=${includeInactive}`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to export emails');
      }

      if (format === 'csv') {
        // Handle CSV download
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `user_emails_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        alert('Email export completed successfully!');
      } else {
        // Handle JSON download
        const data = await res.json();
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `user_emails_export_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        alert('Email export completed successfully!');
      }
    } catch (err) {
      console.error('Failed to export emails:', err);
      alert(err instanceof Error ? err.message : 'Failed to export emails');
    }
  }, [token]);

  const fetchCompanyTokenDetails = useCallback(async (companyId: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/companies/${companyId}/token-usage`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch company token details');
      setCompanyTokenDetails(prev => ({
        ...prev,
        [companyId]: data.data
      }));
    } catch (err) {
      console.error('Failed to fetch company token details:', err);
    }
  }, [token]);

  const toggleCompanyExpansion = useCallback(async (companyId: string) => {
    setExpandedCompanies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
        // Fetch details when expanding
        fetchCompanyTokenDetails(companyId);
      }
      return newSet;
    });
  }, [fetchCompanyTokenDetails]);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (user.role !== 'system_admin') {
      navigate('/', { replace: true });
      return;
    }
    fetchCompanies();
  }, [user, token, navigate, fetchCompanies]);

  const handleDelta = async (companyId: string, delta: number) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/companies/${companyId}/licenses`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ delta }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update licenses');
      setCompanies(prev => prev.map(c => c.id === companyId ? { ...c, licenseCount: data.licenseCount, used: data.used, available: data.available } : c));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error updating licenses');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompany) return;
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/companies/${selectedCompany}/admins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create admin');
      fetchCompanies();
      setSelectedCompany(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error creating admin');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/companies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newCompanyData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create company');
      setCompanies(prev => [...prev, data]);
      setShowCreateCompany(false);
      setNewCompanyData({ name: '', domain: '', licenseCount: 0 });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error creating company');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePasswordChangeSysAdmin = async (userId: string) => {
    const newPassword = prompt('Enter new password (minimum 6 characters):');
    if (newPassword && newPassword.length >= 6) {
      try {
        const res = await fetch(`${API_BASE_URL}/api/sysadmin/users/${userId}/password`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ newPassword }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to change user password');
        }
        alert('User password changed successfully!');
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Error changing user password');
      }
    } else if (newPassword !== null) {
      alert('Password must be at least 6 characters long');
    }
  };

  const resetUser2FASysAdmin = async (userId: string) => {
    if (!window.confirm('Are you sure you want to reset this user\'s two-factor authentication? They will need to set it up again.')) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/sysadmin/users/${userId}/2fa/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to reset user 2FA');
      }
      
      // Refresh users list
      await fetchAllUsers();
      alert('User 2FA reset successfully!');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error resetting user 2FA');
    }
  };

  const handleImpersonateUser = async (userId: string, userName: string) => {
    const confirmed = window.confirm(
      `⚠️ IMPERSONATION MODE\n\n` +
      `You are about to login as "${userName}".\n\n` +
      `This will:\n` +
      `• Grant you full access to their account\n` +
      `• Log all your actions with both identities\n` +
      `• Auto-expire after 30 minutes\n` +
      `• Be recorded in audit logs (SOC2 compliance)\n\n` +
      `Are you sure you want to proceed?`
    );

    if (!confirmed) {
      return;
    }

    try {
      await startImpersonation(userId, 'Support troubleshooting via SystemAdmin portal');
      // Redirect will happen automatically in the context
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start impersonation session');
    }
  };

  const toggleCompanyLockout = async (companyId: string, currentLockStatus: boolean) => {
    const action = currentLockStatus ? 'unlock' : 'lock';
    const message = currentLockStatus
      ? 'Are you sure you want to unlock this company? Users will be able to log in again.'
      : 'Are you sure you want to lock this company? All users will be blocked from logging in until unlocked.';

    if (!window.confirm(message)) {
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/companies/${companyId}/lockout`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ isLocked: !currentLockStatus }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Failed to ${action} company`);
      }

      // Update companies list
      setCompanies(prev => prev.map(c =>
        c.id === companyId ? { ...c, isLocked: !currentLockStatus } : c
      ));

      alert(`Company ${action}ed successfully!`);
    } catch (err) {
      alert(err instanceof Error ? err.message : `Error ${action}ing company`);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleCompanyAMS360 = async (companyId: string, currentStatus: boolean) => {
    const action = currentStatus ? 'disable' : 'enable';
    const message = currentStatus
      ? 'Are you sure you want to disable AMS360 integration for this company? Users will no longer be able to import customers or sync with AMS360.'
      : 'Are you sure you want to enable AMS360 integration for this company? Company admins will be able to configure AMS360 credentials.';

    if (!window.confirm(message)) {
      return;
    }

    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/companies/${companyId}/ams360`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ ams360Enabled: !currentStatus }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Failed to ${action} AMS360`);
      }

      // Update companies list
      setCompanies(prev => prev.map(c =>
        c.id === companyId ? { ...c, ams360Enabled: !currentStatus } : c
      ));

      alert(`AMS360 integration ${action}d successfully!`);
    } catch (err) {
      alert(err instanceof Error ? err.message : `Error ${action}ing AMS360`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCompanyForUser) return;

    setActionLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/companies/${selectedCompanyForUser}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newUserData),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create user');

      alert('User created successfully!');
      setShowCreateUser(false);
      setSelectedCompanyForUser(null);
      setNewUserData({ email: '', firstName: '', lastName: '', role: 'user' });

      // Refresh companies and users
      fetchCompanies();
      if (showAllUsers) {
        fetchAllUsers();
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error creating user');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleUserRole = async (userId: string, currentRole: string) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const message = `Are you sure you want to convert this ${currentRole} to ${newRole}?`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/users/${userId}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ role: newRole }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update user role');
      }

      alert(`User role changed to ${newRole} successfully!`);

      // Refresh users list
      await fetchAllUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error updating user role');
    }
  };

  const deleteUserSysAdmin = async (userId: string, userEmail: string) => {
    const message = `Are you sure you want to delete user "${userEmail}"? This action cannot be undone.`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to delete user');
      }

      alert('User deleted successfully!');

      // Refresh users list
      await fetchAllUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error deleting user');
    }
  };

  const toggleUserLicense = async (userId: string, currentStatus: string, userEmail: string) => {
    const newStatus = currentStatus === 'active' ? 'disabled' : 'active';
    const action = newStatus === 'active' ? 'assign license to' : 'deassign license from';
    const message = `Are you sure you want to ${action} "${userEmail}"?`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/users/${userId}/license`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update user license');
      }

      alert(`User license ${newStatus === 'active' ? 'assigned' : 'deassigned'} successfully!`);

      // Refresh users list
      await fetchAllUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error updating user license');
    }
  };

  // Toggle whether a user consumes a billed SEAT (billing count), distinct
  // from the active/disabled license status above. Any role — sysadmin can
  // flag anyone, unlike the company-admin equivalent (admin-role-only).
  const toggleUserConsumesLicense = async (userId: string, currentConsumesLicense: boolean, userEmail: string) => {
    const next = !currentConsumesLicense;
    const message = `${next ? 'Bill' : 'Stop billing'} a seat for "${userEmail}"?`;

    if (!window.confirm(message)) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/system-admin/users/${userId}/consumes-license`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ consumesLicense: next }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update seat setting');
      }

      await fetchAllUsers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error updating seat setting');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 p-6">{error}</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-secondary dark:text-dark-text">System Administration</h1>
        <button
          onClick={() => navigate('/sysadmin/dashboard')}
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Audit Dashboard
        </button>
      </div>

      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Billing & Revenue */}
        <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Billing & Revenue</h3>
          <div className="space-y-2">
            <button
              onClick={() => { setShowBilling(true); setBillingView('dashboard'); }}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Billing Dashboard
            </button>
            <button
              onClick={() => { setShowTokenUsage(true); fetchTokenUsage(); }}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Token Usage
            </button>
          </div>
        </div>

        {/* Users & Companies */}
        <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Users & Companies</h3>
          <div className="space-y-2">
            <button
              onClick={() => { setShowAllUsers(true); fetchAllUsers(); }}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              All Users
            </button>
            <button
              onClick={() => { setShowActiveUsers(true); fetchActiveUsers(); }}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Active Users
            </button>
            <button
              onClick={() => {
                const options = [
                  { label: 'Export Active Users (CSV)', action: () => exportUserEmails('csv', false) },
                  { label: 'Export All Users (CSV)', action: () => exportUserEmails('csv', true) },
                  { label: 'Export Active Users (JSON)', action: () => exportUserEmails('json', false) },
                  { label: 'Export All Users (JSON)', action: () => exportUserEmails('json', true) }
                ];
                const choice = window.prompt(
                  'Select export option:\n' +
                  options.map((opt, idx) => `${idx + 1}. ${opt.label}`).join('\n') +
                  '\n\nEnter option number (1-4):'
                );
                const optionIndex = parseInt(choice || '') - 1;
                if (optionIndex >= 0 && optionIndex < options.length) {
                  options[optionIndex].action();
                }
              }}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Emails
            </button>
            <button
              onClick={() => setShowCreateCompany(true)}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Company
            </button>
          </div>
        </div>

        {/* Workflows & Support */}
        <div className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Workflows & Support</h3>
          <div className="space-y-2">
            <button
              onClick={() => { setShowWorkflowSettings(true); fetchWorkflowSettings(); }}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Workflow Settings
            </button>
            <button
              onClick={() => { setShowCategoryManager(true); fetchWorkflowCategories(); }}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              Workflow Categories
            </button>
            <button
              onClick={() => setShowGlobalTemplates(true)}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Global Templates
            </button>
            <button
              onClick={() => setShowSupportTickets(true)}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
              </svg>
              Support Tickets
            </button>
            <button
              onClick={() => setShowAgentMailbox(true)}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Agent Mailbox
            </button>
            {features.acord && <button
              onClick={() => setShowAcordRepo(true)}
              className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-4 h-4 mr-3 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              ACORD Forms
            </button>}
          </div>
        </div>
      </div>

      {showCreateCompany && (
        <div className="max-w-md bg-white dark:bg-dark-surface p-6 mb-6 rounded shadow">
          <h2 className="text-lg font-semibold text-secondary dark:text-dark-text mb-4">New Company</h2>
          <form onSubmit={handleCreateCompany} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
              <input
                name="name"
                required
                value={newCompanyData.name}
                onChange={e => setNewCompanyData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Domain</label>
              <input
                name="domain"
                required
                value={newCompanyData.domain}
                onChange={e => setNewCompanyData(prev => ({ ...prev, domain: e.target.value }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Licenses</label>
              <input
                name="licenseCount"
                type="number"
                required
                min={0}
                value={newCompanyData.licenseCount}
                onChange={e => setNewCompanyData(prev => ({ ...prev, licenseCount: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setShowCreateCompany(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600"
              >
                {actionLoading ? 'Creating...' : 'Create Company'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="overflow-x-auto mb-6">
        <table className="min-w-full bg-white dark:bg-dark-surface text-secondary dark:text-dark-text">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left">Company</th>
              <th className="px-4 py-2 text-left">Domain</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Subscription</th>
              <th className="px-4 py-2 text-left">AMS360</th>
              <th className="px-4 py-2 text-left">Licenses (Used/Total)</th>
              <th className="px-4 py-2 text-left">Available</th>
              <th className="px-4 py-2 text-left">Admin Accounts</th>
              <th className="px-4 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {companies.map(c => (
              <React.Fragment key={c.id}>
                <tr className="border-t border-gray-200 dark:border-dark-border">
                  <td className="px-4 py-2">{c.name}</td>
                  <td className="px-4 py-2">{c.domain}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      c.isLocked
                        ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    }`}>
                      {c.isLocked ? 'Locked' : 'Active'}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    {c.subscriptionStatus ? (
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        c.subscriptionStatus === 'active'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : c.subscriptionStatus === 'trialing'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                          : c.subscriptionStatus === 'past_due'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          : c.subscriptionStatus === 'canceled'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {c.subscriptionStatus}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">None</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      disabled={actionLoading}
                      onClick={() => toggleCompanyAMS360(c.id, c.ams360Enabled)}
                      className={`px-2 py-1 text-xs rounded-full font-medium transition-colors ${
                        c.ams360Enabled
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 hover:bg-emerald-200 dark:hover:bg-emerald-800'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                      title={c.ams360Enabled ? 'Click to disable AMS360' : 'Click to enable AMS360'}
                    >
                      {c.ams360Enabled ? 'Enabled' : 'Disabled'}
                    </button>
                  </td>
                  <td className="px-4 py-2">{c.used}/{c.licenseCount}</td>
                  <td className="px-4 py-2">{c.available}</td>
                  <td className="px-4 py-2">
                    {c.admins.length <= 1 ? (
                      c.admins.map(a => (
                        <div key={a.id} className="mb-1">
                          {a.firstName} {a.lastName} ({a.email})
                        </div>
                      ))
                    ) : (
                      <details className="group">
                        <summary className="cursor-pointer text-secondary dark:text-dark-text">
                          {c.admins.length} Admins
                        </summary>
                        <div className="mt-2 space-y-1 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded p-2">
                          {c.admins.map(a => (
                            <div key={a.id} className="text-sm">
                              {a.firstName} {a.lastName} ({a.email})
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </td>
                  <td className="px-4 py-2 space-x-2">
                    <button disabled={actionLoading} onClick={() => handleDelta(c.id, 1)} className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600">+1</button>
                    <button disabled={actionLoading} onClick={() => handleDelta(c.id, -1)} className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600">-1</button>
                    <button
                      disabled={actionLoading}
                      onClick={() => toggleCompanyLockout(c.id, c.isLocked)}
                      className={`px-3 py-1 text-white rounded text-sm ${
                        c.isLocked
                          ? 'bg-green-600 hover:bg-green-700'
                          : 'bg-orange-600 hover:bg-orange-700'
                      }`}
                      title={c.isLocked ? 'Unlock company (allow logins)' : 'Lock company (block logins)'}
                    >
                      {c.isLocked ? 'Unlock' : 'Lock'}
                    </button>
                    <button onClick={() => { setSelectedCompany(c.id); setFormData({ email: '', firstName: '', lastName: '' }); }} className="px-3 py-1 bg-primary text-white rounded hover:bg-blue-600">Create Admin</button>
                    <button
                      onClick={() => {
                        setSelectedCompanyForUser(c.id);
                        setNewUserData({ email: '', firstName: '', lastName: '', role: 'user' });
                        setShowCreateUser(true);
                      }}
                      className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Create User
                    </button>
                    <button
                      onClick={() => setExpandedBillingCompany(expandedBillingCompany === c.id ? null : c.id)}
                      className={`px-3 py-1 text-white rounded text-sm ${
                        expandedBillingCompany === c.id
                          ? 'bg-indigo-700'
                          : 'bg-indigo-600 hover:bg-indigo-700'
                      }`}
                    >
                      {expandedBillingCompany === c.id ? 'Hide Billing' : 'Billing'}
                    </button>
                  </td>
                </tr>
                {expandedBillingCompany === c.id && (
                  <tr>
                    <td colSpan={9} className="px-0 py-0">
                      <div className="bg-gray-50 dark:bg-gray-900/50 border-t border-b border-gray-200 dark:border-gray-700 p-4">
                        <CompanyBillingManagement companyId={c.id} companyName={c.name} />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {selectedCompany && (
        <div className="max-w-md bg-white dark:bg-dark-surface p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-secondary dark:text-dark-text mb-4">Create Admin for Company</h2>
          <form onSubmit={handleCreateAdmin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
              <input
                name="firstName"
                required
                value={formData.firstName}
                onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
              <input
                name="lastName"
                required
                value={formData.lastName}
                onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 p-3 text-sm text-blue-900 dark:text-blue-200">
              A temporary password will be generated and emailed to the new admin. They'll be required to change it on first login. (No email sent if promoting an existing user.)
            </div>
            <div className="flex justify-end space-x-2">
              <button type="button" onClick={() => setSelectedCompany(null)} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">Cancel</button>
              <button type="submit" disabled={actionLoading} className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600">{actionLoading ? 'Saving...' : 'Create Admin'}</button>
            </div>
          </form>
        </div>
      )}

      {/* Create User Form */}
      {showCreateUser && (
        <div className="max-w-md bg-white dark:bg-dark-surface p-6 mb-6 rounded shadow">
          <h2 className="text-lg font-semibold text-secondary dark:text-dark-text mb-4">
            Create User for {companies.find(c => c.id === selectedCompanyForUser)?.name}
          </h2>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
              <input
                name="firstName"
                required
                value={newUserData.firstName}
                onChange={e => setNewUserData(prev => ({ ...prev, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
              <input
                name="lastName"
                required
                value={newUserData.lastName}
                onChange={e => setNewUserData(prev => ({ ...prev, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input
                type="email"
                name="email"
                required
                value={newUserData.email}
                onChange={e => setNewUserData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="rounded-md bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 p-3 text-sm text-blue-900 dark:text-blue-200">
              A temporary password will be generated and emailed to the user. They'll be required to change it on first login.
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
              <select
                name="role"
                value={newUserData.role}
                onChange={e => setNewUserData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateUser(false);
                  setSelectedCompanyForUser(null);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={actionLoading}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                {actionLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Compliance Dashboard */}
      {/* Global Template Manager */}
      <GlobalTemplateManager 
        isVisible={showGlobalTemplates} 
        onClose={() => setShowGlobalTemplates(false)} 
      />

      {/* All Users Dashboard */}
      {showAllUsers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">All Users Management</h2>
              <button
                onClick={() => { setShowAllUsers(false); setUserSearchQuery(''); }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Global User Management
                </h3>
                <p className="text-blue-700 dark:text-blue-200 text-sm">
                  Manage passwords and 2FA settings for users across all companies. Use these features carefully as they affect user security.
                </p>
              </div>

              {/* Search Bar */}
              <div className="mb-4 flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by name, email, or company..."
                    value={userSearchQuery}
                    onChange={(e) => setUserSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                  />
                  {userSearchQuery && (
                    <button
                      onClick={() => setUserSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setCollapsedCompanies(prev => {
                    const companyNames = Array.from(new Set(allUsers.map(u => u.company?.name || 'Unknown')));
                    return prev.size === companyNames.length ? new Set() : new Set(companyNames);
                  })}
                  className="px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors whitespace-nowrap"
                >
                  {(() => {
                    const companyNames = Array.from(new Set(allUsers.map(u => u.company?.name || 'Unknown')));
                    return collapsedCompanies.size === companyNames.length ? 'Expand All' : 'Collapse All';
                  })()}
                </button>
              </div>

              {usersLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {(() => {
                    const query = userSearchQuery.toLowerCase().trim();
                    const filteredUsers = query
                      ? allUsers.filter(u =>
                          `${u.firstName} ${u.lastName}`.toLowerCase().includes(query) ||
                          u.email.toLowerCase().includes(query) ||
                          (u.company?.name || '').toLowerCase().includes(query) ||
                          (u.company?.domain || '').toLowerCase().includes(query)
                        )
                      : allUsers;

                    // Group by company
                    const grouped: Record<string, AdminUser[]> = {};
                    filteredUsers.forEach(user => {
                      const companyName = user.company?.name || 'Unknown';
                      if (!grouped[companyName]) grouped[companyName] = [];
                      grouped[companyName].push(user);
                    });

                    const sortedCompanies = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

                    if (filteredUsers.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          {query ? `No users matching "${userSearchQuery}"` : 'No users found.'}
                        </div>
                      );
                    }

                    return (
                      <>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                          {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} across {sortedCompanies.length} compan{sortedCompanies.length !== 1 ? 'ies' : 'y'}
                          {query && ` matching "${userSearchQuery}"`}
                        </div>
                        {sortedCompanies.map(companyName => {
                          const users = grouped[companyName];
                          const isCollapsed = collapsedCompanies.has(companyName);
                          const companyDomain = users[0]?.company?.domain || 'N/A';

                          return (
                            <div key={companyName} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                              {/* Company Header */}
                              <button
                                onClick={() => setCollapsedCompanies(prev => {
                                  const next = new Set(prev);
                                  if (next.has(companyName)) {
                                    next.delete(companyName);
                                  } else {
                                    next.add(companyName);
                                  }
                                  return next;
                                })}
                                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                              >
                                <div className="flex items-center gap-3">
                                  <svg
                                    className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  <div>
                                    <span className="font-semibold text-gray-900 dark:text-white">{companyName}</span>
                                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{companyDomain}</span>
                                  </div>
                                </div>
                                <span className="px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                                  {users.length} user{users.length !== 1 ? 's' : ''}
                                </span>
                              </button>

                              {/* Users Table */}
                              {!isCollapsed && (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full">
                                    <thead className="bg-gray-50/50 dark:bg-gray-800/50">
                                      <tr>
                                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Role</th>
                                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">LOB</th>
                                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">2FA</th>
                                        <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                      {users.map(user => (
                                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                          <td className="px-6 py-3 whitespace-nowrap">
                                            <div className="flex flex-col">
                                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                {user.firstName} {user.lastName}
                                              </div>
                                              <div className="text-sm text-gray-500 dark:text-gray-300">
                                                {user.email}
                                              </div>
                                            </div>
                                          </td>
                                          <td className="px-6 py-3 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                              user.role === 'system_admin'
                                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                                : user.role === 'admin'
                                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                            } capitalize`}>
                                              {user.role || 'user'}
                                            </span>
                                          </td>
                                          <td className="px-6 py-3 whitespace-nowrap">
                                            {user.role !== 'system_admin' ? (
                                              <select
                                                value={user.lineOfBusiness || 'both'}
                                                onChange={async (e) => {
                                                  await updateUserLineOfBusiness(user.id, e.target.value);
                                                }}
                                                className="block w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-cyan-500 dark:bg-gray-700 dark:text-white"
                                              >
                                                <option value="both">Both</option>
                                                <option value="personal">Personal</option>
                                                <option value="commercial">Commercial</option>
                                              </select>
                                            ) : (
                                              <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                                All
                                              </span>
                                            )}
                                          </td>
                                          <td className="px-6 py-3 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                              user.status === 'active'
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : user.status === 'pending'
                                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                            } capitalize`}>
                                              {user.status}
                                            </span>
                                          </td>
                                          <td className="px-6 py-3 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                              user.twoFactorEnabled
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                            }`}>
                                              {user.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                                            </span>
                                          </td>
                                          <td className="px-6 py-3 whitespace-nowrap text-sm">
                                            <div className="flex flex-wrap gap-1">
                                              {user.role !== 'system_admin' && (
                                                <>
                                                  <button
                                                    onClick={() => handleImpersonateUser(user.id, `${user.firstName} ${user.lastName}`)}
                                                    className="px-2 py-1 text-xs bg-gradient-to-r from-orange-500 to-red-500 text-white rounded hover:from-orange-600 hover:to-red-600 transition-colors font-semibold"
                                                    title="Login as this user (impersonation)"
                                                  >
                                                    🔐 Login As User
                                                  </button>
                                                  <button
                                                    onClick={() => handlePasswordChangeSysAdmin(user.id)}
                                                    className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                                                    title="Change user password"
                                                  >
                                                    Change Password
                                                  </button>
                                                  {user.twoFactorEnabled && (
                                                    <button
                                                      onClick={() => resetUser2FASysAdmin(user.id)}
                                                      className="px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 transition-colors"
                                                      title="Reset user's 2FA"
                                                    >
                                                      Reset 2FA
                                                    </button>
                                                  )}
                                                  <button
                                                    onClick={() => toggleUserRole(user.id, user.role || 'user')}
                                                    className={`px-2 py-1 text-xs text-white rounded transition-colors ${
                                                      user.role === 'admin'
                                                        ? 'bg-blue-500 hover:bg-blue-600'
                                                        : 'bg-indigo-500 hover:bg-indigo-600'
                                                    }`}
                                                    title={`Convert to ${user.role === 'admin' ? 'user' : 'admin'}`}
                                                  >
                                                    Make {user.role === 'admin' ? 'User' : 'Admin'}
                                                  </button>
                                                  <button
                                                    onClick={() => toggleUserLicense(user.id, user.status, user.email)}
                                                    className={`px-2 py-1 text-xs text-white rounded transition-colors ${
                                                      user.status === 'active'
                                                        ? 'bg-orange-500 hover:bg-orange-600'
                                                        : 'bg-green-500 hover:bg-green-600'
                                                    }`}
                                                    title={`${user.status === 'active' ? 'Deassign' : 'Assign'} license`}
                                                  >
                                                    {user.status === 'active' ? 'Deassign License' : 'Assign License'}
                                                  </button>
                                                  <button
                                                    onClick={() => toggleUserConsumesLicense(user.id, user.consumesLicense !== false, user.email)}
                                                    className={`px-2 py-1 text-xs text-white rounded transition-colors ${
                                                      user.consumesLicense !== false
                                                        ? 'bg-teal-500 hover:bg-teal-600'
                                                        : 'bg-gray-400 hover:bg-gray-500'
                                                    }`}
                                                    title={`${user.consumesLicense !== false ? 'Stop billing' : 'Bill'} a seat for this user`}
                                                  >
                                                    {user.consumesLicense !== false ? 'Uses Seat' : 'No Seat'}
                                                  </button>
                                                  <button
                                                    onClick={() => deleteUserSysAdmin(user.id, user.email)}
                                                    className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                                    title="Delete user permanently"
                                                  >
                                                    Delete User
                                                  </button>
                                                </>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Token Usage Dashboard */}
      {showTokenUsage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">AI Token Usage Analytics</h2>
                {tokenUsagePeriod && (
                  <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-sm font-medium rounded-full">
                    {tokenUsagePeriod}
                  </span>
                )}
              </div>
              <button
                onClick={() => setShowTokenUsage(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
                    AI Token Usage Overview
                  </h3>
                  <p className="text-blue-700 dark:text-blue-200 text-sm">
                    Track Gemini API token consumption across all companies and users for the current calendar month.
                    Click on a company to expand and view individual user token usage.
                  </p>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Company
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Input Tokens
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Output Tokens
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Total Tokens
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Users
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Requests / API Calls
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {tokenStats.map((stats) => (
                        <React.Fragment key={stats.companyId}>
                          <tr 
                            className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => toggleCompanyExpansion(stats.companyId)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <svg 
                                  className={`w-4 h-4 mr-2 transition-transform ${
                                    expandedCompanies.has(stats.companyId) ? 'transform rotate-90' : ''
                                  }`} 
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {stats.companyName}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {stats.totalTokensIn.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {stats.totalTokensOut.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900 dark:text-white">
                              {stats.totalTokens.toLocaleString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {stats.userCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                              {stats.requestCount}{stats.totalApiCalls > stats.requestCount ? ` (${stats.totalApiCalls} API calls)` : ''}
                            </td>
                          </tr>

                          {/* Expanded user details */}
                          {expandedCompanies.has(stats.companyId) && companyTokenDetails[stats.companyId] && (
                            <tr>
                              <td colSpan={6} className="px-6 py-0">
                                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 my-2">
                                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                                    User Breakdown for {stats.companyName}
                                  </h4>
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {companyTokenDetails[stats.companyId]?.byUser ? Object.values(companyTokenDetails[stats.companyId].byUser).map((userStats) => (
                                      <div 
                                        key={userStats.userId} 
                                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                                      >
                                        <div className="font-medium text-gray-900 dark:text-white text-sm mb-2">
                                          {userStats.userName}
                                        </div>
                                        <div className="space-y-1 text-xs">
                                          <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">Input:</span>
                                            <span className="text-gray-900 dark:text-white">{userStats.tokensIn.toLocaleString()}</span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-500 dark:text-gray-400">Output:</span>
                                            <span className="text-gray-900 dark:text-white">{userStats.tokensOut.toLocaleString()}</span>
                                          </div>
                                          <div className="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-1">
                                            <span className="text-gray-700 dark:text-gray-300 font-medium">Total:</span>
                                            <span className="text-gray-900 dark:text-white font-medium">{userStats.totalTokens.toLocaleString()}</span>
                                          </div>
                                        </div>
                                      </div>
                                    )) : (
                                      <div className="col-span-full text-center py-4 text-gray-500 dark:text-gray-400">
                                        Loading user details...
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  
                  {tokenStats.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No token usage data available yet. Token usage will appear here once users start using AI features.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Users Dashboard */}
      {showActiveUsers && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Active Users</h2>
                {activeUsersSummary && (
                  <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-300">
                    <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded">
                      {activeUsersSummary.uniqueActiveUsers} users active
                    </span>
                    <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded">
                      {activeUsersSummary.totalActiveSessions} sessions
                    </span>
                    <span className="text-xs">
                      Last {activeUsersSummary.timeframe}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={fetchActiveUsers}
                  className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                >
                  Refresh
                </button>
                <button
                  onClick={() => setShowActiveUsers(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-blue-900 dark:text-blue-100 mb-2">
                  Currently Active Users
                </h3>
                <p className="text-blue-700 dark:text-blue-200 text-sm">
                  Users with active sessions in the last 30 minutes. This shows real-time activity including device information and session details.
                </p>
              </div>

              {activeUsersLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Company</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Device</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">IP Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Last Activity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Session Started</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {activeUsers.map((activeUser) => (
                        <tr key={activeUser.sessionId} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {activeUser.user.firstName} {activeUser.user.lastName}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-gray-300">
                                {activeUser.user.email}
                              </div>
                              <div className="text-xs text-gray-400">
                                <span className={`px-1 py-0.5 rounded text-xs ${
                                  activeUser.user.role === 'system_admin'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                    : activeUser.user.role === 'admin'
                                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>
                                  {activeUser.user.role || 'user'}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {activeUser.user.company ? activeUser.user.company.name : 'Unknown'}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-300">
                              {activeUser.user.company ? activeUser.user.company.domain : 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {activeUser.deviceName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600 dark:text-gray-300">
                            {activeUser.ipAddress}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {activeUser.location || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {new Date(activeUser.lastActivity).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                            {new Date(activeUser.sessionStarted).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {activeUsers.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No active users found in the last 30 minutes.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Support Tickets Manager */}
      {showSupportTickets && (
        <SupportTicketManager onClose={() => setShowSupportTickets(false)} />
      )}

      {/* Agent Mailbox + ACORD repository (AGENT_MAILBOX_PLAN.md §8.3) */}
      {showAgentMailbox && <AgentMailboxAdmin onClose={() => setShowAgentMailbox(false)} />}
      {features.acord && showAcordRepo && <AcordRepositoryAdmin onClose={() => setShowAcordRepo(false)} />}

      {/* Billing Dashboard */}
      {showBilling && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Billing & Subscriptions
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setBillingView('dashboard')}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      billingView === 'dashboard'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Dashboard
                  </button>
                  <button
                    onClick={() => setBillingView('config')}
                    className={`px-3 py-1 text-sm rounded transition-colors ${
                      billingView === 'config'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    Stripe Config
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowBilling(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {billingView === 'dashboard' ? <BillingDashboard /> : <StripeConfiguration />}
            </div>
          </div>
        </div>
      )}

      {/* Workflow Settings Modal */}
      {showWorkflowSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Workflow Settings</h2>
              <button
                onClick={() => setShowWorkflowSettings(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <div className="bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-medium text-cyan-900 dark:text-cyan-100 mb-2">
                  AI Model & Line of Business Configuration
                </h3>
                <p className="text-cyan-700 dark:text-cyan-200 text-sm">
                  Report models are set per tool suite (Cyber, D&amp;O, Home Insurance, …) or per category for classic tools;
                  each suite's automatic router keeps its own model. Users will only see workflows that match their assigned
                  line of business (personal, commercial, or both).
                </p>
              </div>

              {workflowSettingsLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {modelGroups.map((group) => {
                    const allTypes = [...group.reportTypes, ...group.routerTypes];
                    const reportModel = commonSettingValue(group.reportTypes, 'aiModel');
                    const lob = commonSettingValue(allTypes, 'lineOfBusiness');
                    const lossRunTypes = group.reportTypes.filter((t) => t === 'loss_run_analyzer' || t === 'loss_run_analyzer_v4');
                    const expanded = expandedModelGroups.has(group.id);
                    const saving = savingModelGroup === group.id;
                    const selectClass = 'block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-cyan-500 dark:bg-gray-700 dark:text-white disabled:opacity-50';
                    const lastUpdated = allTypes
                      .map((t) => workflowSettings.find((s) => s.workflowType === t)?.updatedAt)
                      .filter(Boolean)
                      .sort()
                      .pop();
                    return (
                      <div key={group.id} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                          <div className="md:col-span-3">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold text-gray-900 dark:text-white">{group.label}</span>
                              {group.kind === 'suite' && (
                                <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">Suite</span>
                              )}
                              {saving && <span className="text-xs text-cyan-600">Saving…</span>}
                            </div>
                            <button
                              type="button"
                              onClick={() => setExpandedModelGroups((prev) => {
                                const next = new Set(prev);
                                if (next.has(group.id)) next.delete(group.id); else next.add(group.id);
                                return next;
                              })}
                              className="text-xs text-cyan-700 dark:text-cyan-400 hover:underline mt-1"
                            >
                              {expanded ? 'Hide' : 'Show'} {allTypes.length} {allTypes.length === 1 ? 'tool' : 'tools'}
                            </button>
                            {lastUpdated && (
                              <div className="text-xs text-gray-400 mt-1">Updated {new Date(lastUpdated).toLocaleDateString()}</div>
                            )}
                          </div>

                          <div className="md:col-span-6 space-y-3">
                            {group.reportTypes.length > 0 && (
                              <div>
                                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                                  Report model{group.reportTypes.length > 1 ? ` (all ${group.reportTypes.length} tools)` : ''}
                                </label>
                                <select
                                  value={reportModel}
                                  disabled={saving}
                                  onChange={async (e) => {
                                    if (!e.target.value) return;
                                    await updateWorkflowSettingsGroup(group.id, group.reportTypes, { aiModel: e.target.value });
                                  }}
                                  className={selectClass}
                                >
                                  {!reportModel && <option value="">Mixed — choose to apply to all</option>}
                                  {renderModelOptions(group.reportTypes, reportModel || null)}
                                </select>
                              </div>
                            )}
                            {lossRunTypes.length > 0 && (
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Loss Run quick model</label>
                                  <select
                                    value={commonSettingValue(lossRunTypes, 'quickAiModel')}
                                    disabled={saving}
                                    onChange={async (e) => {
                                      await updateWorkflowSettingsGroup(group.id, lossRunTypes, { quickAiModel: e.target.value || null });
                                    }}
                                    className="block w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-cyan-500 dark:bg-gray-700 dark:text-white"
                                  >
                                    <option value="">Default (Gemini 2.5 Flash)</option>
                                    {renderModelOptions(lossRunTypes, commonSettingValue(lossRunTypes, 'quickAiModel') || null)}
                                  </select>
                                </div>
                                <div>
                                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Loss Run deep model</label>
                                  <select
                                    value={commonSettingValue(lossRunTypes, 'deepAiModel')}
                                    disabled={saving}
                                    onChange={async (e) => {
                                      await updateWorkflowSettingsGroup(group.id, lossRunTypes, { deepAiModel: e.target.value || null });
                                    }}
                                    className="block w-full px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-cyan-500 dark:bg-gray-700 dark:text-white"
                                  >
                                    <option value="">Default (Gemini 2.5 Pro)</option>
                                    {renderModelOptions(lossRunTypes, commonSettingValue(lossRunTypes, 'deepAiModel') || null)}
                                  </select>
                                </div>
                              </div>
                            )}
                            {group.routerTypes.map((routerType) => {
                              const routerSetting = workflowSettings.find((s) => s.workflowType === routerType);
                              if (!routerSetting) return null;
                              return (
                                <div key={routerType}>
                                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                                    Router model — {formatWorkflowLabel(routerType)}
                                  </label>
                                  <select
                                    value={routerSetting.aiModel}
                                    disabled={saving}
                                    onChange={async (e) => {
                                      await updateWorkflowSetting(routerType, { aiModel: e.target.value });
                                    }}
                                    className={selectClass}
                                  >
                                    {renderModelOptions(routerType, routerSetting.aiModel)}
                                  </select>
                                </div>
                              );
                            })}
                          </div>

                          <div className="md:col-span-3">
                            {group.kind !== 'platform' && (
                              <>
                                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">Line of business</label>
                                <select
                                  value={lob}
                                  disabled={saving}
                                  onChange={async (e) => {
                                    if (!e.target.value) return;
                                    await updateWorkflowSettingsGroup(group.id, allTypes, { lineOfBusiness: e.target.value });
                                  }}
                                  className={selectClass}
                                >
                                  {!lob && <option value="">Mixed — choose to apply to all</option>}
                                  <option value="both">Both (Personal & Commercial)</option>
                                  <option value="personal">Personal Only</option>
                                  <option value="commercial">Commercial Only</option>
                                </select>
                              </>
                            )}
                          </div>
                        </div>

                        {expanded && (
                          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                            {allTypes.map((t) => {
                              const row = workflowSettings.find((s) => s.workflowType === t);
                              return (
                                <div key={t} className="flex items-center justify-between text-xs">
                                  <span className="text-gray-700 dark:text-gray-300">
                                    {formatWorkflowLabel(t)}
                                    {group.routerTypes.includes(t) && <span className="ml-1 text-violet-600 dark:text-violet-400">(router)</span>}
                                  </span>
                                  <span className="text-gray-400 font-mono">{row?.aiModel}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {workflowSettings.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      No workflow settings found. Settings will be initialized when workflows are first used.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Workflow Category Manager Modal */}
      {showCategoryManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full h-5/6 m-4 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Workflow Category Manager</h2>
              <button
                onClick={() => {
                  setShowCategoryManager(false);
                  setEditingCategory(null);
                }}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {categoriesLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left Column: Category Management */}
                  <div className="space-y-6">
                    <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-4">
                      <h3 className="text-lg font-medium text-violet-900 dark:text-violet-100 mb-2">
                        Create New Category
                      </h3>
                      <form onSubmit={createWorkflowCategory} className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                          <input
                            required
                            value={newCategoryData.name}
                            onChange={(e) => setNewCategoryData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="e.g., Claims, Policy Tools"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                          <input
                            value={newCategoryData.description}
                            onChange={(e) => setNewCategoryData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder="Optional description"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Display Order</label>
                          <input
                            type="number"
                            value={newCategoryData.displayOrder}
                            onChange={(e) => setNewCategoryData(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white"
                          />
                        </div>
                        <button
                          type="submit"
                          className="w-full py-2 px-4 bg-violet-600 text-white rounded hover:bg-violet-700 transition-colors"
                        >
                          Create Category
                        </button>
                      </form>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Existing Categories</h3>
                      <div className="space-y-2">
                        {workflowCategories.map((category) => (
                          <div
                            key={category.id}
                            className={`p-4 border rounded-lg ${
                              editingCategory?.id === category.id
                                ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                                : 'border-gray-200 dark:border-gray-700'
                            }`}
                          >
                            {editingCategory?.id === category.id ? (
                              <div className="space-y-2">
                                <input
                                  value={editingCategory.name}
                                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                                />
                                <input
                                  value={editingCategory.description || ''}
                                  onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                                  placeholder="Description"
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                                />
                                <input
                                  type="number"
                                  value={editingCategory.displayOrder}
                                  onChange={(e) => setEditingCategory({ ...editingCategory, displayOrder: parseInt(e.target.value) || 0 })}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => updateWorkflowCategory(category.id, {
                                      name: editingCategory.name,
                                      description: editingCategory.description || undefined,
                                      displayOrder: editingCategory.displayOrder
                                    })}
                                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingCategory(null)}
                                    className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div>
                                  <div className="font-medium text-gray-900 dark:text-white">{category.name}</div>
                                  {category.description && (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">{category.description}</div>
                                  )}
                                  <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                    {category.toolCount} tool{category.toolCount !== 1 ? 's' : ''} | Order: {category.displayOrder}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setEditingCategory(category)}
                                    className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => deleteWorkflowCategory(category.id)}
                                    disabled={category.toolCount > 0}
                                    title={category.toolCount > 0 ? 'Remove all tools first' : 'Delete category'}
                                    className={`px-2 py-1 text-white rounded text-xs ${
                                      category.toolCount > 0
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-red-600 hover:bg-red-700'
                                    }`}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                        {workflowCategories.length === 0 && (
                          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                            No categories found. Create one above.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Tool Assignment */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Tool Category & Visibility Assignment</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Assign categories and visibility settings to each tool. Hidden tools won't appear for any users.
                      Visibility tags control which companies can see each tool.
                    </p>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Tool
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Category
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Hidden
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Visibility
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {toolsWithCategories.map((tool) => (
                            <tr key={tool.workflowType} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${tool.isHidden ? 'opacity-60 bg-red-50 dark:bg-red-900/20' : ''}`}>
                              <td className="px-4 py-3">
                                <div className={`text-sm font-medium ${tool.isHidden ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                                  {tool.displayName}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {tool.workflowType}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <select
                                  value={tool.categoryId || ''}
                                  onChange={(e) => assignToolToCategory(tool.workflowType, e.target.value || null, undefined, undefined)}
                                  disabled={tool.isHidden}
                                  className={`block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white ${tool.isHidden ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <option value="">Uncategorized</option>
                                  {workflowCategories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>
                                      {cat.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <input
                                  type="checkbox"
                                  checked={tool.isHidden || false}
                                  onChange={(e) => assignToolToCategory(tool.workflowType, tool.categoryId, e.target.checked, undefined)}
                                  className="w-4 h-4 text-red-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-red-500 cursor-pointer"
                                  title={tool.isHidden ? 'Click to show this tool' : 'Click to hide this tool'}
                                />
                              </td>
                              <td className="px-4 py-3">
                                <select
                                  value={tool.visibilityTagId || ''}
                                  onChange={(e) => assignToolToCategory(tool.workflowType, tool.categoryId, undefined, e.target.value || null)}
                                  disabled={tool.isHidden}
                                  className={`block w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white ${tool.isHidden ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <option value="">No Tag (visible to all)</option>
                                  {visibilityTags.map((tag) => (
                                    <option key={tag.id} value={tag.id}>
                                      {tag.name}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {toolsWithCategories.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                          No workflow tools found.
                        </div>
                      )}
                    </div>

                    {/* Company Visibility Tags Section */}
                    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Company Visibility Tags</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Assign visibility tags to companies to control which tools they can access.
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            fetchCompaniesWithTags();
                            setShowCompanyVisibilityManager(!showCompanyVisibilityManager);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-md"
                        >
                          {showCompanyVisibilityManager ? 'Hide' : 'Manage Company Tags'}
                        </button>
                      </div>

                      {showCompanyVisibilityManager && (
                        <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                          <table className="min-w-full">
                            <thead>
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                  Company
                                </th>
                                {visibilityTags.filter(t => t.name !== 'Everyone').map((tag) => (
                                  <th key={tag.id} className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                                    {tag.name}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {companiesWithTags.map((company) => (
                                <tr key={company.companyId} className="hover:bg-gray-100 dark:hover:bg-gray-800">
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                                    {company.companyName}
                                  </td>
                                  {visibilityTags.filter(t => t.name !== 'Everyone').map((tag) => (
                                    <td key={tag.id} className="px-4 py-3 text-center">
                                      <input
                                        type="checkbox"
                                        checked={company.tagIds.includes(tag.id)}
                                        onChange={(e) => {
                                          const newTagIds = e.target.checked
                                            ? [...company.tagIds, tag.id]
                                            : company.tagIds.filter(id => id !== tag.id);
                                          updateCompanyVisibilityTags(company.companyId, newTagIds);
                                        }}
                                        className="w-4 h-4 text-violet-600 bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-violet-500 cursor-pointer"
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {companiesWithTags.length === 0 && (
                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                              No companies found. Loading...
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemAdmin;